import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  MenuItem,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';

import {
  useGetAvailableAgencyShiftsQuery,
  useGrabAgencyShiftMutation,
} from '../slices/agencyShiftApiSlice';
import WorkerAssignDialog from '../components/WorkerAssignDialog';

// Shifts enter this list automatically — there is no admin "release" step.
// The list is simply "open, unfilled, and inside my agency's window", which
// the backend recomputes on every request, so a shift can appear or
// disappear here purely by the clock moving (a shift rolling into the
// window) or by another agency grabbing it first.
const AvailableShiftsScreen = () => {
  const { data: shifts, isLoading, isFetching, error, refetch } =
    useGetAvailableAgencyShiftsQuery(undefined, {
      pollingInterval: 30000, // first-come-first-served — keep this fresh
    });

  const [grabShift, { isLoading: isGrabbing }] = useGrabAgencyShiftMutation();
  const [dialogShift, setDialogShift] = useState(null);

  // Ward/category filters are client-side over whatever's already in this
  // agency's window — the list is small enough (and already polled every
  // 30s) that there's no need for a round trip just to filter it further.
  const [wardFilter, setWardFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');

  const wardOptions = useMemo(
    () => [...new Set((shifts || []).map((s) => s.wardDepartmentArea).filter(Boolean))].sort(),
    [shifts]
  );
  const categoryOptions = useMemo(
    () => [...new Set((shifts || []).map((s) => s.categoryOfStaff).filter(Boolean))].sort(),
    [shifts]
  );

  const filteredShifts = useMemo(
    () =>
      (shifts || []).filter(
        (s) =>
          (!wardFilter || s.wardDepartmentArea === wardFilter) &&
          (!categoryFilter || s.categoryOfStaff === categoryFilter)
      ),
    [shifts, wardFilter, categoryFilter]
  );

  const handleGrabSubmit = async ({ workerName, workerContact, workerEmail }) => {
    try {
      await grabShift({
        shiftId: dialogShift._id,
        workerName,
        workerContact,
        workerEmail,
      }).unwrap();
      toast.success('Shift Fulfilled successfully');
      setDialogShift(null);
    } catch (err) {
      // 409 = another agency won the race — the list has already been
      // invalidated/refetched by RTK Query's error handling below.
      toast.error(err?.data?.message || 'Could not Supply this shift');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ flexGrow: 1, fontWeight: 700 }}>
          Available Shifts
        </Typography>
        <IconButton onClick={() => refetch()} disabled={isFetching}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.data?.message || 'Failed to load available shifts'}
        </Alert>
      )}

      {!isLoading && !!shifts?.length && (
        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            label="Ward"
            size="small"
            value={wardFilter}
            onChange={(e) => setWardFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All wards</MenuItem>
            {wardOptions.map((ward) => (
              <MenuItem key={ward} value={ward}>
                {ward}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Category"
            size="small"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">All categories</MenuItem>
            {categoryOptions.map((category) => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !shifts?.length ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No shifts are currently available within your agency's visibility window.
          </Typography>
        </Paper>
      ) : !filteredShifts.length ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No available shifts match the selected filters.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Table — sm and up */}
          <Paper sx={{ display: { xs: 'none', sm: 'block' } }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Ward</TableCell>
                  <TableCell>Ref</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Special Requirements</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredShifts.map((shift) => {
                  const start = new Date(shift.startDateTime);
                  const end = new Date(shift.endDateTime);
                  return (
                    <TableRow key={shift._id} hover>
                      <TableCell>{start.toLocaleDateString()}</TableCell>
                      <TableCell>
                        {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        {' – '}
                        {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell>{shift.wardDepartmentArea}</TableCell>
                      {/* A shift only gets a PO/Ref number once an agency grabs it
                          (see generatePoNumber() in agencyShiftController.js's
                          grabShift) — so this is normally blank here and only
                          appears once it shows up on the My Shifts screen. */}
                      <TableCell>{shift.poNumber || '—'}</TableCell>
                      <TableCell>{shift.categoryOfStaff}</TableCell>
                      <TableCell>{shift.specialRequirements || '—'}</TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => setDialogShift(shift)}
                        >
                          Supply
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Paper>

          {/* Cards — below sm, same data as the table above */}
          <Stack spacing={1.5} sx={{ display: { xs: 'flex', sm: 'none' } }}>
            {filteredShifts.map((shift) => {
              const start = new Date(shift.startDateTime);
              const end = new Date(shift.endDateTime);
              return (
                <Paper key={shift._id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{shift.wardDepartmentArea}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {shift.poNumber || '—'}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {start.toLocaleDateString()} · {start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {' – '}
                    {end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {shift.categoryOfStaff}
                  </Typography>
                  {shift.specialRequirements && (
                    <Typography variant="body2" color="text.secondary">
                      {shift.specialRequirements}
                    </Typography>
                  )}
                  <Divider sx={{ my: 1.5 }} />
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => setDialogShift(shift)}
                  >
                    Supply
                  </Button>
                </Paper>
              );
            })}
          </Stack>
        </>
      )}

      <WorkerAssignDialog
        open={!!dialogShift}
        onClose={() => setDialogShift(null)}
        onSubmit={handleGrabSubmit}
        isSubmitting={isGrabbing}
        shift={dialogShift}
        title="Supply this shift"
        submitLabel="Confirm"
      />
    </Box>
  );
};

export default AvailableShiftsScreen;
